/**
 * Revision Requests Panel
 * 
 * Manages revision requests with filtering, priority indicators,
 * deadline tracking, and completion status.
 */

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { Badge } from '../UI/Badge';
import { LoadingSpinner } from '../UI/LoadingSpinner';
import { Input } from '../UI/Input';
import { Select } from '../UI/Select';
import { 
  FileEdit, 
  CheckCircle, 
  Clock, 
  AlertTriangle,
  Filter,
  Plus,
  X
} from 'lucide-react';
import { 
  createRevisionRequest,
  getRevisionRequests,
  getRevisionSummary,
  completeRevisionRequest,
  type RevisionRequest
} from '../../api/reviewApi';
import { useWorkspace } from '../../hooks/useWorkspace';
import { getPolicyPack } from '../../api/policy';
import { getProject } from '../../api/projects';

export interface RevisionRequestsPanelProps {
  reportId: string;
  onRevisionChange?: () => void;
}

export function RevisionRequestsPanel({ 
  reportId, 
  onRevisionChange 
}: RevisionRequestsPanelProps) {
  const { projectId } = useWorkspace();
  const queryClient = useQueryClient();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [filterSection, setFilterSection] = useState<string>('all');
  const [priorities, setPriorities] = useState<Record<string, any>>({
    // Default priorities (fallback if policy pack doesn't define them)
    critical: { name: 'Critical', description: 'Blocks re-submission', blocks_re_submission: true },
    important: { name: 'Important', description: 'Should be addressed before re-submission', blocks_re_submission: false },
    optional: { name: 'Optional', description: 'Nice to have improvements', blocks_re_submission: false },
  });
  
  // Form state
  const [sectionReference, setSectionReference] = useState('');
  const [issueDescription, setIssueDescription] = useState('');
  const [requiredChanges, setRequiredChanges] = useState('');
  const [priority, setPriority] = useState<'critical' | 'important' | 'optional'>('important');
  const [deadline, setDeadline] = useState('');
  
  // Fetch report sections for dropdown
  const { data: sectionsData } = useQuery({
    queryKey: ['report-sections', reportId],
    queryFn: () => getReportSections(reportId, { includeCustom: true, orderBy: 'display_order' }),
    enabled: !!reportId,
  });
  
  const reportSections = sectionsData?.sections || [];

  // Load policy pack for priorities
  useEffect(() => {
    if (!projectId) return;
    
    // First fetch project to get policy_pack_id
    getProject(projectId).then((project) => {
      if (project.policy_pack_id) {
        return getPolicyPack(project.policy_pack_id);
      }
      return null;
    }).then((pack) => {
      if (pack) {
        const reviewWorkflow = pack?.review_workflow;
        if (reviewWorkflow?.revision_request_priorities) {
          setPriorities(reviewWorkflow.revision_request_priorities);
        }
      }
    }).catch((err) => {
      console.warn('[Revision Requests] Failed to load policy pack:', err);
    });
  }, [projectId]);

  // Get revision requests
  const { data: requests, isLoading: requestsLoading } = useQuery({
    queryKey: ['revision-requests', reportId],
    queryFn: () => getRevisionRequests(reportId),
    refetchOnWindowFocus: false,
  });

  // Get revision summary
  const { data: summary } = useQuery({
    queryKey: ['revision-summary', reportId],
    queryFn: () => getRevisionSummary(reportId),
    refetchOnWindowFocus: false,
  });

  // Create revision request mutation
  const createMutation = useMutation({
    mutationFn: () => createRevisionRequest(
      reportId,
      sectionReference || undefined,
      issueDescription,
      requiredChanges,
      priority,
      deadline ? new Date(deadline).toISOString() : undefined
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['revision-requests', reportId] });
      queryClient.invalidateQueries({ queryKey: ['revision-summary', reportId] });
      queryClient.invalidateQueries({ queryKey: ['review-history', reportId] });
      setSectionReference('');
      setIssueDescription('');
      setRequiredChanges('');
      setPriority('important');
      setDeadline('');
      setShowCreateForm(false);
      if (onRevisionChange) {
        onRevisionChange();
      }
      alert('Revision request created successfully!');
    },
    onError: (error: any) => {
      alert(`Failed to create revision request: ${error.message || 'Unknown error'}`);
    },
  });

  // Complete revision request mutation
  const completeMutation = useMutation({
    mutationFn: (requestId: string) => completeRevisionRequest(reportId, requestId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['revision-requests', reportId] });
      queryClient.invalidateQueries({ queryKey: ['revision-summary', reportId] });
      if (onRevisionChange) {
        onRevisionChange();
      }
      alert('Revision request marked as completed!');
    },
    onError: (error: any) => {
      alert(`Failed to complete revision request: ${error.message || 'Unknown error'}`);
    },
  });

  const handleCreate = () => {
    if (!issueDescription.trim()) {
      alert('Please provide an issue description');
      return;
    }
    if (!requiredChanges.trim()) {
      alert('Please describe the required changes');
      return;
    }
    createMutation.mutate();
  };

  const handleComplete = (requestId: string) => {
    if (confirm('Mark this revision request as completed?')) {
      completeMutation.mutate(requestId);
    }
  };

  // Filter requests
  const filteredRequests = requests?.filter((req: RevisionRequest) => {
    if (filterStatus !== 'all' && req.status !== filterStatus) return false;
    if (filterPriority !== 'all' && req.priority !== filterPriority) return false;
    if (filterSection !== 'all' && req.section_reference !== filterSection) return false;
    return true;
  }) || [];

  // Get unique sections for filter
  const sections = Array.from(new Set(requests?.map((r: RevisionRequest) => r.section_reference).filter(Boolean) || []));

  if (requestsLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
          <span className="ml-3 text-gray-600 dark:text-gray-400">Loading revision requests...</span>
        </div>
      </Card>
    );
  }

  const priorityColors: Record<string, string> = {
    critical: 'error',
    important: 'warning',
    optional: 'info',
  };

  const statusColors: Record<string, string> = {
    open: 'default',
    in_progress: 'info',
    completed: 'success',
    cancelled: 'error',
  };

  return (
    <Card>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold mb-2">Revision Requests</h3>
          {summary && (
            <div className="flex items-center gap-4 text-sm">
              <Badge variant={summary.completion_status.critical_completed ? 'success' : 'error'}>
                {summary.completion_status.critical_pending} Critical Pending
              </Badge>
              <Badge variant={summary.completion_status.all_completed ? 'success' : 'warning'}>
                {summary.completion_status.total_pending} Total Pending
              </Badge>
            </div>
          )}
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={() => setShowCreateForm(!showCreateForm)}
        >
          <Plus className="h-4 w-4 mr-2" />
          {showCreateForm ? 'Cancel' : 'New Request'}
        </Button>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <div className="mb-6 p-4 bg-theme-surface rounded-lg border border-theme-border space-y-4">
          <h4 className="font-medium">Create Revision Request</h4>
          
          <div>
            <label className="block text-sm font-medium mb-2">Section Reference (Optional)</label>
            <Select
              value={sectionReference}
              onChange={(e) => setSectionReference(e.target.value)}
              options={[
                { value: '', label: 'General (No Specific Section)' },
                ...reportSections.map((section) => ({
                  value: section.section_name,
                  label: section.section_name,
                })),
              ]}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Issue Description *</label>
            <textarea
              className="w-full px-3 py-2 border border-theme-border rounded-lg bg-white dark:bg-theme-paper text-gray-900 dark:text-theme-text focus:outline-none focus:ring-2 focus:ring-primary-500"
              value={issueDescription}
              onChange={(e) => setIssueDescription(e.target.value)}
              placeholder="Describe the issue that needs to be addressed..."
              rows={3}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Required Changes *</label>
            <textarea
              className="w-full px-3 py-2 border border-theme-border rounded-lg bg-white dark:bg-theme-paper text-gray-900 dark:text-theme-text focus:outline-none focus:ring-2 focus:ring-primary-500"
              value={requiredChanges}
              onChange={(e) => setRequiredChanges(e.target.value)}
              placeholder="Describe the specific changes required..."
              rows={3}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Priority</label>
              <Select
                value={priority}
                onChange={(e) => setPriority(e.target.value as 'critical' | 'important' | 'optional')}
                options={Object.entries(priorities).map(([key, value]: [string, any]) => ({
                  value: key,
                  label: `${value.name || key}${value.blocks_re_submission ? ' (Blocks re-submission)' : ''}`,
                }))}
              />
              {priorities[priority] && (
                <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                  {priorities[priority].description}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Deadline (Optional)</label>
              <Input
                type="date"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={() => {
                setShowCreateForm(false);
                setSectionReference('');
                setIssueDescription('');
                setRequiredChanges('');
                setPriority('important');
                setDeadline('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleCreate}
              disabled={createMutation.isPending || !issueDescription.trim() || !requiredChanges.trim()}
            >
              {createMutation.isPending ? 'Creating...' : 'Create Request'}
            </Button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mb-4 flex gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-500" />
          <Select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            options={[
              { value: 'all', label: 'All Statuses' },
              { value: 'open', label: 'Open' },
              { value: 'in_progress', label: 'In Progress' },
              { value: 'completed', label: 'Completed' },
              { value: 'cancelled', label: 'Cancelled' },
            ]}
          />
        </div>
        <Select
          value={filterPriority}
          onChange={(e) => setFilterPriority(e.target.value)}
          options={[
            { value: 'all', label: 'All Priorities' },
            { value: 'critical', label: 'Critical' },
            { value: 'important', label: 'Important' },
            { value: 'optional', label: 'Optional' },
          ]}
        />
        {sections.length > 0 && (
          <Select
            value={filterSection}
            onChange={(e) => setFilterSection(e.target.value)}
            options={[
              { value: 'all', label: 'All Sections' },
              ...sections.map((section) => ({ value: section, label: section })),
            ]}
          />
        )}
      </div>

      {/* Revision Requests List */}
      <div className="space-y-3">
        {filteredRequests.length > 0 ? (
          filteredRequests.map((request: RevisionRequest) => (
            <div
              key={request.id}
              className="p-4 border border-theme-border rounded-lg bg-theme-surface"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Badge variant={priorityColors[request.priority] || 'default'} size="sm">
                    {request.priority}
                  </Badge>
                  <Badge variant={statusColors[request.status] || 'default'} size="sm">
                    {request.status}
                  </Badge>
                  {request.section_reference && (
                    <Badge variant="info" size="sm">
                      {request.section_reference}
                    </Badge>
                  )}
                </div>
                {request.status === 'open' || request.status === 'in_progress' ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleComplete(request.id)}
                    disabled={completeMutation.isPending}
                  >
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Mark Complete
                  </Button>
                ) : null}
              </div>

              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-medium">Issue: </span>
                  <span className="text-gray-700 dark:text-gray-300">{request.issue_description}</span>
                </div>
                <div>
                  <span className="font-medium">Required Changes: </span>
                  <span className="text-gray-700 dark:text-gray-300">{request.required_changes}</span>
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-600 dark:text-gray-400">
                  {request.requested_by_name && (
                    <span>Requested by: {request.requested_by_name}</span>
                  )}
                  {request.created_at && (
                    <span>Created: {new Date(request.created_at).toLocaleString()}</span>
                  )}
                  {request.deadline && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Deadline: {new Date(request.deadline).toLocaleDateString()}
                    </span>
                  )}
                  {request.completed_at && (
                    <span className="flex items-center gap-1 text-green-600">
                      <CheckCircle className="h-3 w-3" />
                      Completed: {new Date(request.completed_at).toLocaleString()}
                    </span>
                  )}
                  {request.completed_by_name && (
                    <span>Completed by: {request.completed_by_name}</span>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-8 text-gray-600 dark:text-gray-400">
            <FileEdit className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No revision requests found</p>
            {filterStatus !== 'all' || filterPriority !== 'all' || filterSection !== 'all' ? (
              <p className="text-sm mt-1">Try adjusting your filters</p>
            ) : (
              <p className="text-sm mt-1">Click "New Request" to create one</p>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
